import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Chip,
  Divider,
  Drawer,
  Group,
  MultiSelect,
  NumberInput,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  DATA_SOURCES,
  LANGUAGES,
  LOCALES,
  PassioRequestHeaders,
  PassioSearchConfig,
  REGION_CODES,
  SOURCE_FILTERS,
  TYPE_FILTERS,
  type DataSourceName,
  type SourceFilter,
  type TypeFilter,
} from "@passio/api-client";
import { passio } from "../lib/clients";
import { resetClientHeaders, setClientHeaders, useClientHeaders } from "../lib/clientSettings";
import { AI_ROUTES, modelOptions, setRouteModels, useRouteModels } from "../lib/models";
import { useSession } from "../lib/session";
import { CodeBlock } from "./JsonView";
import { ErrorBoundary } from "./ErrorBoundary";
import { useModelCatalog } from "../lib/modelInfo";
import { ModelSelect } from "./ModelPicker";

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Stack gap={8}>
      <Group gap={6}>
        <Text fw={700} fz="sm">
          {title}
        </Text>
        {hint && (
          <Tooltip label={hint} multiline w={300}>
            <IconInfoCircle size={15} color="var(--mantine-color-dimmed)" />
          </Tooltip>
        )}
      </Group>
      {children}
    </Stack>
  );
}

/**
 * Editable fields for every optional request header. `catalogs` loads the tag and concern
 * catalogs through the proxy (needs a signed-in user with the reference feature).
 */
export function ClientHeadersFields({ value: draft, onChange: setDraft, catalogs = false }: { value: PassioRequestHeaders; onChange: (h: PassioRequestHeaders) => void; catalogs?: boolean }) {
  const sc = draft.searchConfig;
  const update = (fn: (c: PassioSearchConfig) => void) => {
    const next = sc.clone();
    fn(next);
    setDraft(draft.with({ searchConfig: next }));
  };

  const reference = catalogs;
  const tags = useQuery({ queryKey: ["ref", "tags"], queryFn: async () => (await passio.foods.tags({ quiet: true })).data, enabled: reference, staleTime: Infinity });
  const concerns = useQuery({ queryKey: ["ref", "concerns"], queryFn: async () => (await passio.foods.concernCodes({ quiet: true })).data, enabled: reference, staleTime: Infinity });
  const tagOptions = tags.data?.groups?.map((g) => ({ group: g.label ?? g.key ?? "Tags", items: g.tags ?? [] })) ?? tags.data?.tags ?? [];
  const concernOptions = (concerns.data ?? []).filter((c) => c.code !== undefined).map((c) => ({ value: String(c.code), label: `${c.code} · ${c.name ?? ""}` }));

  const problems = sc.validate();
  const regions = Object.entries(sc.regionFilters);

  return (
    <>
      <Section title="Language" hint="Localization-ISO: translates response text (food names, descriptions). English skips translation.">
        <Select
          searchable
          data={LANGUAGES.map((l) => ({ value: l.value, label: `${l.label} (${l.value})` }))}
          value={draft.localizationIso ?? "en"}
          onChange={(v) => setDraft(draft.with({ localizationIso: v ?? "en" }))}
          allowDeselect={false}
        />
      </Section>

      <Section title="Regional context" hint="Passio-Product-Localization: routes to a regional search index (with fallback) and gives AI tools regional context.">
        <Select
          searchable
          clearable
          placeholder="Global (no regional routing)"
          data={LOCALES.map((l) => ({ value: l.value, label: `${l.label} · ${l.value}` }))}
          value={draft.productLocalization ?? null}
          onChange={(v) => setDraft(new PassioRequestHeaders({ ...draft.toInit(), productLocalization: v ?? undefined }))}
        />
      </Section>

      <Switch
        label="Product & brand images"
        description="X-Passio-Product-Images: adds images.product / images.brand to lookup results."
        checked={draft.productImages}
        onChange={(e) => setDraft(draft.with({ productImages: e.currentTarget.checked }))}
      />

      <Divider label="Passio-Search-Config" labelPosition="left" />

      <Section title="Data sources" hint="How many results to request from each source. Unset = server default; 0 disables the source.">
        <Stack gap={6}>
          {DATA_SOURCES.map((d) => {
            const v = sc.dataSources[d.value as DataSourceName];
            return (
              <Group key={d.value} gap="xs" wrap="nowrap">
                <Switch
                  checked={v !== undefined}
                  onChange={(e) =>
                    update((c) => {
                      if (e.currentTarget.checked) c.dataSources[d.value] = d.default;
                      else delete c.dataSources[d.value];
                    })
                  }
                  aria-label={`Override ${d.label}`}
                />
                <Text fz="sm" style={{ flex: 1 }}>
                  {d.label}
                  <Text span c="dimmed" fz="xs">
                    {" "}
                    · default {d.default}, max {d.max}
                  </Text>
                </Text>
                <NumberInput
                  w={96}
                  size="xs"
                  min={0}
                  max={d.max}
                  disabled={v === undefined}
                  value={v ?? d.default}
                  onChange={(n) => update((c) => void (c.dataSources[d.value] = Number(n) || 0))}
                />
              </Group>
            );
          })}
        </Stack>
      </Section>

      <Section title="Item types" hint="typeFilters: only return these kinds of items. None selected = all.">
        <Chip.Group multiple value={sc.typeFilters} onChange={(v) => update((c) => void (c.typeFilters = v as TypeFilter[]))}>
          <Group gap={6}>
            {TYPE_FILTERS.map((t) => (
              <Chip key={t.value} value={t.value} size="sm" color="violet">
                {t.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </Section>

      <Section title="Region filters" hint="regionFilters: only foods available in these regions. Priority 1 = highest, 10 = lowest. Keys can be ISO countries, BCP 47 tags or language codes.">
        <Stack gap={6}>
          {regions.map(([code, prio], i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <Select
                style={{ flex: 1 }}
                size="xs"
                searchable
                data={[...REGION_CODES.map((r) => ({ value: r.value, label: `${r.label} (${r.value})` })), ...(REGION_CODES.some((r) => r.value === code) ? [] : [{ value: code, label: code }])]}
                value={code}
                onChange={(v) =>
                  update((c) => {
                    const entries = Object.entries(c.regionFilters);
                    entries[i] = [v ?? code, prio];
                    c.regionFilters = Object.fromEntries(entries);
                  })
                }
                allowDeselect={false}
              />
              <NumberInput size="xs" w={80} min={1} max={10} value={prio} onChange={(n) => update((c) => void (c.regionFilters[code] = Number(n) || 1))} aria-label="Priority" />
              <ActionIcon variant="subtle" color="gray" onClick={() => update((c) => void delete c.regionFilters[code])} aria-label="Remove region">
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="light"
            color="ink"
            size="xs"
            leftSection={<IconPlus size={14} />}
            w="fit-content"
            onClick={() =>
              update((c) => {
                const next = REGION_CODES.find((r) => !(r.value in c.regionFilters));
                if (next) c.regionFilters[next.value] = Object.keys(c.regionFilters).length + 1;
              })
            }
          >
            Add region
          </Button>
          <SegmentedControl
            size="xs"
            value={sc.strictRegionMode === null ? "default" : String(sc.strictRegionMode)}
            onChange={(v) => update((c) => void (c.strictRegionMode = v === "default" ? null : v === "true"))}
            data={[
              { value: "default", label: "Server default" },
              { value: "true", label: "Strict: drop items w/o country" },
              { value: "false", label: "Keep items w/o country" },
            ]}
          />
        </Stack>
      </Section>

      <Section title="Tags" hint="tagFilters: foods must carry ALL of these. excludeTagFilters: drop foods with ANY of these. See Reference > Tags for the catalog.">
        <TagsInput
          label="Must have"
          placeholder={reference ? "Pick or type tags" : "Type tags"}
          data={tagOptions}
          value={sc.tagFilters}
          onChange={(v) => update((c) => void (c.tagFilters = v))}
          clearable
          maxDropdownHeight={260}
        />
        <TagsInput
          label="Exclude"
          placeholder="e.g. contains_peanuts"
          data={tagOptions}
          value={sc.excludeTagFilters}
          onChange={(v) => update((c) => void (c.excludeTagFilters = v))}
          clearable
          maxDropdownHeight={260}
        />
      </Section>

      <Section title="Data sources within Passio" hint="sourceFilters: keep only Passio results from these origins (doesn't affect Edamam).">
        <MultiSelect
          data={SOURCE_FILTERS.map((s) => ({ value: s.value, label: s.label }))}
          value={sc.sourceFilters}
          onChange={(v) => update((c) => void (c.sourceFilters = v as SourceFilter[]))}
          placeholder="All origins"
          clearable
        />
      </Section>

      <Section title="Hide results with concern flags" hint="ignoreWithConcernFlags: remove results tagged with these concern codes.">
        <MultiSelect
          data={concernOptions}
          value={sc.ignoreWithConcernFlags.map(String)}
          onChange={(v) => update((c) => void (c.ignoreWithConcernFlags = v.map(Number)))}
          placeholder={reference ? "Pick concern codes" : "Requires reference access"}
          disabled={!reference}
          searchable
          clearable
        />
      </Section>

      <Section title="Prefer catalog" hint="preferCatalogCustomerHash: a ranking boost (not a filter) for foods added in Catalog Manager. 'self' = your own catalog.">
        <Group gap="xs" wrap="nowrap">
          <TextInput
            style={{ flex: 1 }}
            placeholder="self or 16-hex hash"
            value={sc.preferCatalogCustomerHash}
            onChange={(e) => {
              const v = e.currentTarget.value;
              update((c) => void (c.preferCatalogCustomerHash = v));
            }}
          />
          <Button size="sm" variant="default" onClick={() => update((c) => void (c.preferCatalogCustomerHash = "self"))}>
            self
          </Button>
        </Group>
      </Section>

      {problems.length > 0 && (
        <Alert color="orange" title="Check these">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Alert>
      )}

      <Section title="Header preview">
        <CodeBlock code={draft.preview().map((h) => `${h.name}: ${h.value}`).join("\n") || "(no optional headers: server defaults)"} maxHeight={220} language="http" />
      </Section>
    </>
  );
}

function RouteModelsFields({ value, onChange }: { value: Record<string, string>; onChange: (m: Record<string, string>) => void }) {
  const q = useModelCatalog();
  const custom = AI_ROUTES.filter((r) => value[r.id]).length;
  return (
    <Section title="AI models per route" hint="Sent as ?model=<tag> on each AI route. Route default = the model Passio runs for that route. Each AI page also has an AI model card for its own routes, and the Models page compares them all.">
      <Group gap="xs" wrap="nowrap">
        <Select
          size="xs"
          style={{ flex: 1 }}
          searchable
          placeholder="Set every route to…"
          data={modelOptions(q.data ?? [], "")}
          value={null}
          onChange={(v) => v && onChange(Object.fromEntries(AI_ROUTES.map((r) => [r.id, v])))}
        />
        <Button size="xs" variant="default" disabled={!custom} onClick={() => onChange({})}>
          All defaults
        </Button>
      </Group>
      <Stack gap={10}>
        {AI_ROUTES.map((r) => (
          <ModelSelect key={r.id} routeId={r.id} label={r.title} size="xs" details={false} value={value[r.id] ?? ""} onChange={(t) => onChange({ ...value, [r.id]: t })} />
        ))}
      </Stack>
    </Section>
  );
}

function SettingsForm({ onDone }: { onDone: () => void }) {
  const current = useClientHeaders();
  const currentModels = useRouteModels();
  const { hasFeature } = useSession();
  const [draft, setDraft] = useState<PassioRequestHeaders>(current);
  const [models, setModels] = useState(currentModels);
  return (
    <Stack gap="lg">
      <Text c="dimmed" fz="sm">
        These optional headers are sent with every Passio call from this browser. The inspector shows exactly what went out.
      </Text>
      <ClientHeadersFields value={draft} onChange={setDraft} catalogs={hasFeature("reference")} />
      <Divider />
      <RouteModelsFields value={models} onChange={setModels} />
      <Group
        justify="space-between"
        p="md"
        style={{ position: "sticky", bottom: 0, zIndex: 2, marginInline: "calc(var(--mantine-spacing-md) * -1)", marginBottom: "calc(var(--mantine-spacing-md) * -1)", background: "white", borderTop: "1px solid var(--mantine-color-default-border)" }}
      >
        <Button
          variant="subtle"
          color="gray"
          onClick={() => {
            resetClientHeaders();
            setRouteModels({});
            onDone();
          }}
        >
          Reset to defaults
        </Button>
        <Group gap="xs">
          <Button variant="default" onClick={onDone}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setClientHeaders(draft);
              setRouteModels(models);
              onDone();
            }}
          >
            Save settings
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

export function SettingsDrawer({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="left"
      size={460}
      title={<Text fw={700}>Client settings</Text>}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {opened && (
        <ErrorBoundary label="Client settings" inspector>
          <SettingsForm onDone={onClose} />
        </ErrorBoundary>
      )}
    </Drawer>
  );
}
